import React, { useState } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { Box, Stack, Inline, Text, Pressable } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';

import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_INSIGHTS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import { useAudioStream } from '../../hooks/useAudioStream';
import { useAudioEventListener } from '../../hooks/useAudioEventListener';
import { useAudioBriefing } from '../../hooks/useAudioBriefing';

import { AudioBriefingChangeboarding } from './AudioBriefingChangeboarding';
import { AudioBriefingPopupControlButton } from './AudioBriefingPopupControlButton';
import { AudioBriefingPopupListenPanel } from './panels/AudioBriefingPopupListenPanel';
import { AudioBriefingPopupCustomizePanel } from './panels/customize/AudioBriefingPopupCustomizePanel';
import { AudioBriefingPopupSourcesPanel } from './panels/AudioBriefingPopupSourcesPanel';
import { AudioBriefingPopupFeedbackFooter } from './AudioBriefingPopupFeedbackFooter';
import { AudioTimeIndicator } from '../controls/AudioTimeIndicator';

const i18n = defineMessages({
	listenTab: {
		id: 'audio.audio-briefing-popup.listen-tab',
		defaultMessage: 'Listen',
		description:
			'The name of the tab in the audio briefing popup that directs towards the miniplayer to listen to audio',
	},
	customizeTab: {
		id: 'audio.audio-briefing-popup.customize-tab',
		defaultMessage: 'Customize',
		description:
			'The name of the tab in the audio briefing popup that directs towards the customization panel',
	},
	sourcesTab: {
		id: 'audio.audio-briefing-popup.sources-tab',
		defaultMessage: 'Sources',
		description:
			'The name of the tab in the audio briefing popup that directs towards the sources panel',
	},
	briefingTitle: {
		id: 'audio.audio-briefing-popup.briefing-title',
		defaultMessage: 'Briefing: {contentTitle}',
		description: 'Title of the audio briefing popup which takes in the actual page title',
	},
	briefingBylineCasualDescription: {
		id: 'audio.audio-briefing-popup.briefing-byline-casual-description',
		defaultMessage: 'Casual',
		description: 'Byline of the audio briefing popup title that shows the tone of the briefing',
	},
	briefingBylineDetailDescription: {
		id: 'audio.audio-briefing-popup.briefing-byline-detail-description',
		defaultMessage: 'High-level overview',
		description:
			'Byline of the audio briefing popup title that shows the level of detail of the briefing',
	},
	closeButtonLabel: {
		id: 'audio.audio-briefing-popup.close-label',
		defaultMessage: 'Close',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will close the audio briefings popup',
	},
});

type AudioBriefingPopupContentProps = {
	closePopup: () => void;
};

type MinimizedPlayerProps = {
	expandPopup: () => void;
	audioRef: React.RefObject<HTMLAudioElement>;
	isPlaying: boolean;
};

const styles = cssMap({
	wrapper: {
		width: '300px',
	},
	tabList: {
		marginBottom: token('space.150'),
		marginTop: token('space.150'),
	},
	footer: {
		backgroundColor: token('elevation.surface.sunken'),
	},
	headerTitle: {
		flex: 1,
	},
	minimizedWrapper: {
		width: '105px',
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
});

const MinimizedPlayer = ({
	expandPopup,
	audioRef,
	isPlaying,
}: MinimizedPlayerProps): JSX.Element => {
	return (
		<Pressable
			xcss={styles.minimizedWrapper}
			onClick={expandPopup}
			testId="briefings-minimized-player"
		>
			<Box padding="space.050">
				<AudioTimeIndicator audioRef={audioRef} isPlaying={isPlaying} variant="blue" />
			</Box>
		</Pressable>
	);
};

export const AudioBriefingPopupContent = ({ closePopup }: AudioBriefingPopupContentProps) => {
	const { formatMessage } = useIntl();

	const [
		{ activeContent, stream, isPlaying },
		{ pauseContent, resumeContent, endContent, setContentError },
	] = useAudioBriefing();
	const { audioRef } = useAudioStream(stream, setContentError);
	useAudioEventListener({
		audioRef,
		onContentPaused: pauseContent,
		onContentResumed: resumeContent,
		onContentEnded: endContent,
	});

	const [isMinimized, setIsMinimized] = useState(false);

	const contentTitle = activeContent?.contentTitle;

	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState(
		ONBOARDING_INSIGHTS.AUDIO_BRIEFING,
	);

	const { hasAudioBriefingChangeboardingBeenSeen } = deserializeState(onboardingStateData);

	if (onboardingStateLoading) {
		return null;
	}

	if (!hasAudioBriefingChangeboardingBeenSeen) {
		return <AudioBriefingChangeboarding />;
	}

	return (
		<>
			{/* eslint-disable-next-line jsx-a11y/media-has-caption -- The summary is already available in text so the track isn't very necessary */}
			<audio data-testid="briefings-audio" ref={audioRef} />
			{isMinimized ? (
				<MinimizedPlayer
					expandPopup={() => {
						setIsMinimized(false);
					}}
					audioRef={audioRef}
					isPlaying={isPlaying}
				/>
			) : (
				<Box xcss={styles.wrapper}>
					<Stack>
						<Box padding="space.200">
							<Inline grow="fill" space="space.150">
								<Stack xcss={styles.headerTitle}>
									<Heading size="xsmall">
										{formatMessage(i18n.briefingTitle, { contentTitle })}
									</Heading>
									<Text color="color.text.subtlest" size="small">
										<Inline alignBlock="center" space="space.050" separator="·">
											<FormattedMessage {...i18n.briefingBylineCasualDescription} />
											<FormattedMessage {...i18n.briefingBylineDetailDescription} />
										</Inline>
									</Text>
								</Stack>

								{/* Renders a close button if the audio is not playing, 
								otherwise renders a minimize button */}
								<AudioBriefingPopupControlButton
									isPlaying={isPlaying}
									minimizePopup={() => {
										setIsMinimized(true);
									}}
									closePopup={closePopup}
								/>
							</Inline>

							<Tabs id="audio-tabs">
								<Box xcss={styles.tabList}>
									<TabList>
										<Tab>{formatMessage(i18n.listenTab)}</Tab>
										<Tab>{formatMessage(i18n.customizeTab)}</Tab>
										<Tab>{formatMessage(i18n.sourcesTab)}</Tab>
									</TabList>
								</Box>
								<TabPanel>
									<AudioBriefingPopupListenPanel audioRef={audioRef} />
								</TabPanel>
								<TabPanel>
									<AudioBriefingPopupCustomizePanel />
								</TabPanel>
								<TabPanel>
									<AudioBriefingPopupSourcesPanel />
								</TabPanel>
							</Tabs>
						</Box>
						<Box xcss={styles.footer} paddingBlock="space.100" paddingInline="space.200">
							<AudioBriefingPopupFeedbackFooter activeContent={activeContent} />
						</Box>
					</Stack>
				</Box>
			)}
		</>
	);
};
